import React, { useState, useEffect } from 'react';
import api from '../api/axios';

const Trivia = () => {
    const [polls, setPolls] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPoll, setEditingPoll] = useState(null);
    const [formData, setFormData] = useState({ 
        question: '', 
        sala_id: '',
        options: [{ text: '', isCorrect: false }, { text: '', isCorrect: false }] 
    });
    const [isLoading, setIsLoading] = useState(true);
    const [salas, setSalas] = useState([]);

    useEffect(() => {
        fetchPolls();
    }, []);

    const fetchPolls = async () => {
        try {
            const [pollsRes, salasRes] = await Promise.all([
                api.get('/polls/all'),
                api.get('/salas')
            ]);
            setPolls(pollsRes.data);
            setSalas(salasRes.data);
        } catch (error) {
            console.error("Error fetching polls or salas", error);
        } finally {
            setIsLoading(false);
        }
    };

    const openModal = async (poll = null) => {
        if (poll) {
            try {
                const res = await api.get(`/polls/${poll.id}`);
                const fullPoll = res.data;
                setEditingPoll(fullPoll);
                setFormData({ 
                    question: fullPoll.question, 
                    sala_id: fullPoll.sala_id || '',
                    options: fullPoll.options.map(o => ({
                        text: o.option_text,
                        isCorrect: !!o.is_correct
                    }))
                });
            } catch (error) {
                console.error("Error fetching poll details", error);
                return;
            }
        } else {
            setEditingPoll(null);
            setFormData({ 
                question: '', 
                sala_id: '',
                options: [{ text: '', isCorrect: false }, { text: '', isCorrect: false }] 
            });
        }
        setIsModalOpen(true);
    };

    const handleSavePoll = async (e) => {
        e.preventDefault();
        const filteredOptions = formData.options.filter(o => o.text.trim() !== '');
        if (filteredOptions.length < 2) return alert("Mínimo 2 opciones");
        
        const submissionData = {
            question: formData.question,
            sala_id: formData.sala_id || null,
            options: filteredOptions.map(o => ({
                option_text: o.text,
                is_correct: o.isCorrect
            }))
        };

        try {
            if (editingPoll) {
                await api.put(`/polls/${editingPoll.id}`, submissionData);
            } else {
                await api.post('/polls', submissionData);
            }
            setIsModalOpen(false);
            fetchPolls();
        } catch (error) {
            console.error("Error saving poll", error);
        }
    };

    const toggleStatus = async (id, status) => {
        try {
            await api.put(`/polls/${id}/status`, { status });
            fetchPolls();
        } catch (error) {
            console.error("Error toggling status", error);
        }
    };

    const addOption = () => setFormData({ ...formData, options: [...formData.options, { text: '', isCorrect: false }] });
    const removeOption = (index) => {
        if (formData.options.length <= 2) return;
        setFormData({ ...formData, options: formData.options.filter((_, i) => i !== index) });
    };

    const setCorrectOption = (index) => {
        const newOptions = formData.options.map((opt, i) => ({
            ...opt,
            isCorrect: i === index
        }));
        setFormData({ ...formData, options: newOptions });
    };

    if (isLoading) return <div className="p-8 text-center">Cargando trivia...</div>;

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <header className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-black text-slate-800 uppercase italic tracking-tighter">Gestión de Trivia</h1>
                    <p className="text-slate-500">Crea y gestiona las preguntas interactivas en vivo.</p>
                </div>
                <button 
                    onClick={() => openModal()}
                    className="bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg"
                >
                    + Nueva Pregunta
                </button>
            </header>

            <div className="grid gap-4">
                {polls.map(p => (
                    <div key={p.id} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex justify-between items-center">
                        <div>
                            <h3 className="font-bold text-slate-800 text-lg">{p.question}</h3>
                            <div className="flex gap-4 mt-2">
                                <span className={`text-[10px] font-black uppercase px-2 py-1 rounded ${p.status === 'active' ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                                    {p.status}
                                </span>
                                {salas.length > 0 && (
                                    <span className="text-[10px] text-indigo-600 font-black uppercase tracking-widest bg-indigo-50 px-2 py-1 rounded">
                                        Sala: {p.sala_name || 'Global'}
                                    </span>
                                )}
                                <span className="text-[10px] text-slate-400 uppercase font-bold tracking-widest">Creada: {new Date(p.created_at).toLocaleDateString()}</span>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => openModal(p)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all">✏️</button>
                            {p.status !== 'active' ? (
                                <button onClick={() => toggleStatus(p.id, 'active')} className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest">Activar</button>
                            ) : (
                                <button onClick={() => toggleStatus(p.id, 'closed')} className="bg-red-600 text-white px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest">Cerrar</button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-3xl w-full max-w-xl shadow-2xl overflow-hidden animate-fadeIn">
                        <form onSubmit={handleSavePoll} className="p-8 space-y-6">
                            <h2 className="text-2xl font-black text-slate-800 italic uppercase tracking-tighter">
                                {editingPoll ? 'Editar Pregunta' : 'Nueva Pregunta'}
                            </h2>

                            {salas.length > 0 && (
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-400 mb-2 tracking-widest">Sala Asociada</label>
                                    <select 
                                        className="w-full bg-slate-50 border-0 rounded-xl p-4 text-slate-800 font-bold focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                                        value={formData.sala_id}
                                        onChange={e => setFormData({...formData, sala_id: e.target.value})}
                                    >
                                        <option value="">Global / Todas las salas</option>
                                        {salas.map(s => (
                                            <option key={s.id} value={s.id}>{s.name} {s.ubication ? `(${s.ubication})` : ''}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            
                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-400 mb-2 tracking-widest">Pregunta</label>
                                <textarea 
                                    className="w-full bg-slate-50 border-0 rounded-xl p-4 text-slate-800 font-bold focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                                    value={formData.question}
                                    onChange={e => setFormData({...formData, question: e.target.value})}
                                    placeholder="¿Cuál es la respuesta correcta?"
                                    required
                                />
                            </div>

                            <div className="space-y-4">
                                <label className="flex justify-between items-center text-[10px] font-black uppercase text-slate-400 tracking-widest">
                                    Opciones
                                    <button type="button" onClick={addOption} className="text-blue-600">+ Añadir</button>
                                </label>
                                {formData.options.map((opt, i) => (
                                    <div key={i} className="flex gap-3 items-center">
                                        <div className="flex flex-col items-center">
                                            <input 
                                                type="radio" 
                                                name="correct"
                                                checked={opt.isCorrect}
                                                onChange={() => setCorrectOption(i)}
                                                className="w-4 h-4 accent-emerald-500 cursor-pointer"
                                                required
                                            />
                                            <span className="text-[8px] font-black uppercase text-slate-400">Correcta</span>
                                        </div>
                                        <input 
                                            className="flex-grow bg-slate-50 border-0 rounded-xl p-3 text-slate-700 font-medium"
                                            value={opt.text}
                                            onChange={e => {
                                                const opts = [...formData.options];
                                                opts[i].text = e.target.value;
                                                setFormData({...formData, options: opts});
                                            }}
                                            placeholder={`Opción ${i + 1}`}
                                            required
                                        />
                                        {formData.options.length > 2 && (
                                            <button type="button" onClick={() => removeOption(i)} className="text-red-400 hover:text-red-600">✕</button>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="flex gap-4 pt-4">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="flex-grow py-3 rounded-xl text-slate-400 font-bold hover:bg-slate-50 transition-all">Cancelar</button>
                                <button type="submit" className="flex-grow bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg">Guardar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Trivia;
